import { forwardRef } from 'react'
import type {
  ComponentPropsWithoutRef,
  ElementRef,
  JSX,
  MouseEventHandler,
  ReactElement,
  ReactNode,
} from 'react'

import {
  Button,
  IconButton,
  Maximize2Icon,
  Minimize2Icon,
  Tooltip,
  XIcon,
} from '@buffer-mono/popcorn'

import styles from './Header.module.css'

export function ComposerHeaderPanelButton({
  label,
  active,
  controls,
  onClick,
  onMouseEnter,
  onFocus,
  children,
}: {
  label: string
  active: boolean
  controls?: string
  onClick: MouseEventHandler<HTMLButtonElement>
  onMouseEnter?: () => void
  onFocus?: () => void
  children: ReactNode
}): JSX.Element {
  return (
    <Tooltip content={label}>
      <Button
        variant="tertiary"
        aria-pressed={active}
        aria-label={label}
        aria-controls={controls}
        className={styles.sidePanelButton}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onFocus={onFocus}
      >
        {children} <span className={styles.buttonLabel}>{label}</span>
      </Button>
    </Tooltip>
  )
}

/** Icon-only: the label names the unread count, the tooltip adds mentions. */
export function ComposerHeaderNotesButton({
  label,
  tooltip,
  active,
  onClick,
  children,
}: {
  label: string
  tooltip: string
  active: boolean
  onClick: MouseEventHandler<HTMLButtonElement>
  children: ReactElement
}): JSX.Element {
  return (
    <IconButton
      variant="tertiary"
      label={label}
      tooltip={tooltip}
      aria-pressed={active}
      onClick={onClick}
    >
      {children}
    </IconButton>
  )
}

export function ComposerHeaderMaximizeButton({
  isMaximized,
  onToggle,
}: {
  isMaximized: boolean
  onToggle: () => void
}): JSX.Element {
  const label = isMaximized ? 'Exit fullscreen' : 'Enter fullscreen'

  return (
    <IconButton
      variant="tertiary"
      label={label}
      tooltip={label}
      className={styles.maximizeButton}
      onClick={onToggle}
    >
      {isMaximized ? <Minimize2Icon /> : <Maximize2Icon />}
    </IconButton>
  )
}

export const ComposerHeaderCloseButton = forwardRef<
  ElementRef<typeof IconButton>,
  Omit<ComponentPropsWithoutRef<typeof IconButton>, 'children' | 'label'>
>((props, ref) => (
  <IconButton
    {...props}
    tooltip="Close Composer"
    ref={ref}
    label="Close Composer"
    variant="tertiary"
  >
    <XIcon />
  </IconButton>
))

ComposerHeaderCloseButton.displayName = 'ComposerHeaderCloseButton'
